import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Box,
  IconButton,
  TablePagination,
  Alert,
  CircularProgress,
  Tooltip,
  LinearProgress,
  Typography,
  Chip,
  Grid,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  CardActions,
  Stack
} from '@mui/material';
import { DeleteOutline, VisibilityOutlined, CloudUpload, InsertDriveFile, PlayArrow, Close } from '@mui/icons-material';
import './DriverViolations.css';

const AttachmentsPopup = ({ violationId, onClose }) => {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [queue, setQueue] = useState([]); // upload queue: {id,file,progress,status,controller,error}
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [message, setMessage] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewIsVideo, setPreviewIsVideo] = useState(false);
  const fileInputRef = useRef(null);
  const MAX_FILE_SIZE_DEFAULT = 100 * 1024 * 1024; // 100 MB for non-video files
  const MAX_VIDEO_SIZE = 25 * 1024 * 1024; // 25 MB per video
  const SUPPORTED_TYPES_DESC = 'Images, Videos (max 25MB each), PDF, Word, Excel, PowerPoint';

  useEffect(() => {
    if (violationId) fetchAttachments();
  }, [violationId]);

  const fetchAttachments = async () => {
    try {
      const token = localStorage.getItem('token');
      const resp = await axios.get(`${process.env.REACT_APP_BASE_API_URL}/driver-violations/${violationId}/attachments`, { headers: { Authorization: `Bearer ${token}` } });
      setAttachments(resp.data || []);
    } catch (err) {
      console.error('Error fetching attachments:', err);
      setMessage('Error loading attachments');
    }
  };

  // Helpers for improved upload UX (queue, drag-drop, per-file progress)
  const enqueueFiles = (files) => {
    if (!files || !files.length) return;
    // Build sets for deduplication
    const existingQueueSigs = new Set(
      queue.map((q) => {
        const f = q.file || {};
        return `${(f.name||'').toLowerCase()}|${f.size||0}|${f.lastModified||0}`;
      })
    );
    const uploadedNames = new Set(attachments.map((a) => (a.FILE_NAME || a.file_name || '').toLowerCase()));

    const items = [];
    let dupFound = false;

    files.forEach((f) => {
      const name = (f.name || '').toLowerCase();
      const ext = (name.split('.').pop() || '').toLowerCase();
      const sig = `${name}|${f.size||0}|${f.lastModified||0}`;

      if (existingQueueSigs.has(sig)) {
        dupFound = true;
        return; // skip duplicate already in queue
      }

      if (uploadedNames.has(name)) {
        dupFound = true;
        return; // skip file already uploaded to server (by name)
      }

      const isVideo = f.type.startsWith('video') || ['mp4','webm','ogg','mov','mkv'].includes(ext);
      const maxAllowed = isVideo ? MAX_VIDEO_SIZE : MAX_FILE_SIZE_DEFAULT;

      const id = `${Date.now()}-${Math.random().toString(36).slice(2,9)}`;
      const item = { id, file: f, progress: 0, status: 'queued', controller: null, error: null };

      if (f.size > maxAllowed) {
        item.status = 'error';
        item.error = isVideo ? 'Video too large (max 25MB)' : 'File too large (max 100MB)';
      } else {
        // validate mime/extension for common office formats
        const allowed = (
          f.type.startsWith('image/') ||
          f.type.startsWith('video/') ||
          f.type === 'application/pdf' ||
          f.type === 'application/msword' ||
          f.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
          f.type === 'application/vnd.ms-excel' ||
          f.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
          f.type === 'application/vnd.ms-powerpoint' ||
          f.type === 'application/vnd.openxmlformats-officedocument.presentationml.presentation' ||
          ['doc','docx','xls','xlsx','ppt','pptx','pdf'].includes(ext)
        );

        if (!allowed) {
          item.status = 'error';
          item.error = 'Unsupported file type';
        }
      }

      items.push(item);
      existingQueueSigs.add(sig);
    });

    if (dupFound) {
      setMessage('Some files were skipped because they are duplicates or already uploaded.');
      setTimeout(() => setMessage(''), 4000);
    }

    if (items.length === 0) return;

    setQueue((q) => [...q, ...items]);
    // reset file input so same file can be re-selected if skipped
    try {
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (e) {
      console.warn('Failed to reset file input', e);
    }
    // start uploading the newly enqueued items immediately (use items array to avoid state timing)
    items.forEach((it) => {
      uploadItem(it);
    });
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files || []);
    enqueueFiles(files);
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    enqueueFiles(files);
  };

  const startQueue = async () => {
    // replaced by uploadAllQueued / uploadItem for simpler, more reliable per-file uploads
  };

  // Upload a single queued item
  const uploadItem = async (item) => {
    if (!item || !item.file) return;
    // if already uploading or done, skip
    if (item.status !== 'queued') return;

    const controller = new AbortController();
    setQueue((qarr) => qarr.map((it) => (it.id === item.id ? { ...it, status: 'uploading', controller } : it)));

    try {
      const token = localStorage.getItem('token');
      const fd = new FormData();
      fd.append('file', item.file);

      await axios.post(
        `${process.env.REACT_APP_BASE_API_URL}/driver-violations/${violationId}/attachments`,
        fd,
        {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
          signal: controller.signal,
          onUploadProgress: (progressEvent) => {
            const total = progressEvent.total || (item.file && item.file.size) || 0;
            const loaded = progressEvent.loaded || 0;
            const pct = total > 0 ? Math.round((loaded * 100) / total) : 0;
            setQueue((qarr) => qarr.map((it) => (it.id === item.id ? { ...it, progress: pct } : it)));
          },
        }
      );

      setQueue((qarr) => qarr.map((it) => (it.id === item.id ? { ...it, status: 'done', progress: 100 } : it)));
      // refresh attachments list and show confirmation
      await fetchAttachments();
      setMessage('Upload completed');
      setTimeout(() => setMessage(''), 2500);
      // remove completed item from queue after short delay so uploaded state is visible briefly
      setTimeout(() => {
        setQueue((qarr) => qarr.filter((it) => it.id !== item.id));
      }, 1200);
    } catch (err) {
      console.error('Upload error:', err);
      if (err.name === 'CanceledError' || err.message === 'canceled') {
        setQueue((qarr) => qarr.map((it) => (it.id === item.id ? { ...it, status: 'canceled' } : it)));
      } else {
        const serverMsg = err?.response?.data?.message || err.message || 'Error uploading file';
        setQueue((qarr) => qarr.map((it) => (it.id === item.id ? { ...it, status: 'error', error: serverMsg } : it)));
      }
    }
  };

  // Start uploads for all queued items (concurrent)
  const uploadAllQueued = () => {
    const toUpload = queue.filter((q) => q.status === 'queued');
    toUpload.forEach((it) => {
      // start each upload without awaiting to allow concurrency
      uploadItem(it);
    });
  };

  const handleCancelUpload = (id) => {
    const item = queue.find((q) => q.id === id);
    if (!item) return;
    if (item.controller) item.controller.abort();
    setQueue((qarr) => qarr.map((it) => (it.id === id ? { ...it, status: 'canceled' } : it)));
  };

  const openPreview = (url, isVideo = false) => {
    setPreviewUrl(url);
    setPreviewIsVideo(isVideo);
    setPreviewOpen(true);
  };

  const closePreview = () => {
    setPreviewOpen(false);
    setPreviewUrl('');
    setPreviewIsVideo(false);
  };

  

  const handleDelete = async (attId) => {
    if (!window.confirm('Delete this attachment?')) return;
    try {
      const token = localStorage.getItem('token');
      // find attachment name to sync queue
      const target = attachments.find((a) => (a.ATTACHMENT_ID || a.attachment_id || '') === attId);
      const targetName = (target && (target.FILE_NAME || target.file_name || '')).toString().toLowerCase();

      await axios.delete(`${process.env.REACT_APP_BASE_API_URL}/driver-violations/attachments/${attId}`, { headers: { Authorization: `Bearer ${token}` } });
      // remove any queued items that match the deleted file name
      if (targetName) {
        setQueue((qarr) => qarr.filter((it) => ((it.file && it.file.name || '').toLowerCase()) !== targetName));
      }
      setMessage('Attachment deleted successfully');
      setTimeout(() => setMessage(''), 2000);
      await fetchAttachments();
    } catch (err) {
      console.error('Delete error:', err);
      setMessage('Error deleting attachment');
    }
  };

  const handleRemoveQueuedItem = (queueId) => {
    if (!window.confirm('Remove this file from upload queue?')) return;
    setQueue((qarr) => qarr.filter((it) => it.id !== queueId));
    setMessage('Removed file from upload queue');
    setTimeout(() => setMessage(''), 1800);
  };

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedData = attachments.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const queuedVideoCount = queue.filter((q) => {
    const name = (q.file && q.file.name || '').toLowerCase();
    const ext = (name.split('.').pop() || '').toLowerCase();
    return (q.file && q.file.type && q.file.type.startsWith('video')) || ['mp4','webm','ogg','mov','mkv'].includes(ext);
  }).length;

  const uploadedVideoCount = attachments.filter((a) => {
    const fileName = a.FILE_NAME || a.file_name || '';
    const ext = (fileName.split('.').pop() || '').toLowerCase();
    return (a.MIME_TYPE || '').startsWith('video') || ['mp4','webm','ogg','mov','mkv'].includes(ext);
  }).length;

  const hasActiveQueue = queue.some((q) => ['queued', 'uploading'].includes(q.status));

  return (
    <Dialog open={true} onClose={onClose} maxWidth="md" fullWidth fullScreen={isSmall}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontWeight: 'bold', bgcolor: '#f3f4f6', borderBottom: '1px solid #e5e7eb' }}>
        <span>📎 Violation Attachments — ID #{violationId}</span>
        <IconButton aria-label="close" onClick={onClose} size={isSmall ? 'large' : 'small'}>
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        {message && (
          <Alert severity={message.includes('Error') ? 'error' : 'success'} sx={{ mb: 2 }} onClose={() => setMessage('')}>
            {message}
          </Alert>
        )}

        <Box
          sx={{ mb: 2 }}
        >
          <Typography variant="body2" sx={{ mb: 1 }}>Supported: {SUPPORTED_TYPES_DESC}</Typography>
          <Box
            onDrop={handleDrop}
            onDragOver={(e) => e.preventDefault()}
            sx={{ p: 2, border: '2px dashed #cbd5e1', borderRadius: 2, mb: 2 }}
          >
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={8}>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,video/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
                  onChange={handleFileChange}
                  multiple
                  style={{ display: 'none' }}
                />
                <Typography variant="caption" color="textSecondary">Or drag & drop files here</Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Button
                  variant="contained"
                  startIcon={<CloudUpload />}
                  onClick={() => fileInputRef.current && fileInputRef.current.click()}
                >
                  Select Files
                </Button>
              </Grid>
            </Grid>

            {/* quick video counts (only show when there are videos) */}
            {(queuedVideoCount > 0 || uploadedVideoCount > 0) && (
              <Box sx={{ mt: 1, display: 'flex', gap: 1, alignItems: 'center' }}>
                {queuedVideoCount > 0 && <Chip label={`Videos queued: ${queuedVideoCount}`} size="small" />}
                {uploadedVideoCount > 0 && <Chip label={`Videos uploaded: ${uploadedVideoCount}`} color="success" size="small" />}
              </Box>
            )}

            {hasActiveQueue && (
              <Box sx={{ mt: 2 }}>
                {queue.map((qitem) => (
                  <Box key={qitem.id} sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 1 }}>
                    <Box sx={{ width: 72, height: 48, bgcolor: '#fff', borderRadius: 1, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #e5e7eb' }}>
                      {qitem.file.type.startsWith('image/') && (
                        <img src={URL.createObjectURL(qitem.file)} alt="thumb" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      )}
                      {qitem.file.type.startsWith('video/') && (
                        <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
                          <video src={URL.createObjectURL(qitem.file)} style={{ width: '100%', height: '100%', objectFit: 'cover' }} muted />
                          <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <PlayArrow sx={{ color: '#fff', bgcolor: 'rgba(0,0,0,0.5)', borderRadius: '50%' }} />
                          </Box>
                        </Box>
                      )}
                      {qitem.file.type === 'application/pdf' && <InsertDriveFile />}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontWeight: 600 }}>{qitem.file.name}</Typography>
                      <Typography variant="caption" color="textSecondary">{(qitem.file.size/1024/1024).toFixed(2)} MB</Typography>
                      <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ flex: 1 }}>
                          <LinearProgress variant="determinate" value={qitem.progress} sx={{ height: 6, borderRadius: 1 }} />
                        </Box>
                        <Typography variant="caption" sx={{ minWidth: 40, textAlign: 'right' }}>{qitem.progress}%</Typography>
                      </Box>
                      {qitem.status === 'error' && <Typography color="error" variant="caption">{qitem.error}</Typography>}
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'flex-end' }}>
                      {qitem.status === 'uploading' && (
                        <Button size="small" color="inherit" onClick={() => handleCancelUpload(qitem.id)}>Cancel</Button>
                      )}
                      {qitem.status === 'queued' && (
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Chip label="Queued" size="small" />
                          <IconButton size="small" onClick={() => handleRemoveQueuedItem(qitem.id)} title="Remove from queue">
                            <DeleteOutline fontSize="small" />
                          </IconButton>
                        </Box>
                      )}
                      {qitem.status === 'done' && <Chip label="Uploaded" color="success" size="small" />}
                      {qitem.status === 'error' && (
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Chip label="Error" color="error" size="small" />
                          <IconButton size="small" onClick={() => handleRemoveQueuedItem(qitem.id)} title="Remove from queue">
                            <DeleteOutline fontSize="small" />
                          </IconButton>
                        </Box>
                      )}
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Box>

        {attachments.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4, color: '#999' }}>
            No attachments yet. Upload files above.
          </Box>
        ) : (
          <>
            {isSmall ? (
              <Stack spacing={1}>
                {paginatedData.map((a, idx) => {
                  const fileName = a.FILE_NAME || a.file_name || 'Untitled';
                  const fileUrl = a.FILE_URL || a.file_url || '#';
                  const createdAt = a.CREATED_AT ? new Date(a.CREATED_AT).toLocaleDateString() : '—';
                  const ext = (fileName.split('.').pop() || '').toLowerCase();
                  const isVideo = ['mp4','webm','ogg','mov','mkv'].includes(ext) || (a.MIME_TYPE || '').startsWith('video');
                  const isImage = ['jpg','jpeg','png','gif','bmp','webp'].includes(ext) || (a.MIME_TYPE || '').startsWith('image');

                  return (
                    <Card key={a.ATTACHMENT_ID || idx} variant="outlined">
                      <CardContent sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        <Box sx={{ width: 92, height: 64, flexShrink: 0 }}>
                          {isImage && <img src={fileUrl} alt={fileName} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 6 }} />}
                          {isVideo && <video src={fileUrl} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 6 }} muted preload="metadata" />}
                          {!isImage && !isVideo && <InsertDriveFile />}
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography sx={{ fontWeight: 700 }}>{fileName}</Typography>
                          <Typography variant="caption" color="textSecondary">{createdAt} • {ext.toUpperCase()}</Typography>
                        </Box>
                        <CardActions sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <Button size="small" onClick={() => openPreview(fileUrl, isVideo)} fullWidth>View</Button>
                          <Button size="small" color="error" onClick={() => handleDelete(a.ATTACHMENT_ID)} fullWidth>Delete</Button>
                        </CardActions>
                      </CardContent>
                    </Card>
                  );
                })}
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={attachments.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    sx={{ borderTop: '1px solid #e5e7eb', width: '100%' }}
                  />
                </Box>
              </Stack>
            ) : (
              <>
                {/* Removed filename filter UI */}

                <TableContainer sx={{ border: '1px solid #e5e7eb', borderRadius: 1 }}>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f3f4f6' }}>
                        <TableCell sx={{ fontWeight: 'bold', width: '8%', textAlign: 'center' }}>#</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', width: '45%' }}>File Name</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', width: '20%', textAlign: 'center' }}>Uploaded</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', width: '27%', textAlign: 'center' }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedData.map((a, idx) => {
                        const fileName = a.FILE_NAME || a.file_name || 'Untitled';
                        const fileUrl = a.FILE_URL || a.file_url || '#';
                        const createdAt = a.CREATED_AT ? new Date(a.CREATED_AT).toLocaleDateString() : '—';
                        const ext = (fileName.split('.').pop() || '').toLowerCase();
                        const isVideo = ['mp4','webm','ogg','mov','mkv'].includes(ext) || (a.MIME_TYPE || '').startsWith('video');
                        const isImage = ['jpg','jpeg','png','gif','bmp','webp'].includes(ext) || (a.MIME_TYPE || '').startsWith('image');

                        return (
                          <TableRow key={a.ATTACHMENT_ID || idx} sx={{ '&:hover': { bgcolor: '#f9f9f9' } }}>
                            <TableCell sx={{ textAlign: 'center' }}>{page * rowsPerPage + idx + 1}</TableCell>
                            <TableCell sx={{ wordBreak: 'break-word' }}>
                              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                <Box>
                                  {isImage && (
                                    <img src={fileUrl} alt={fileName} style={{ width: 100, height: 60, objectFit: 'cover', borderRadius: 4 }} />
                                  )}
                                  {isVideo && (
                                    <video src={fileUrl} width={140} height={80} style={{ objectFit: 'cover', borderRadius: 4 }} preload="metadata" controls={false} />
                                  )}
                                </Box>
                                <Box>
                                  <a href={fileUrl} target="_blank" rel="noreferrer" style={{ color: '#0066cc', textDecoration: 'none', fontWeight: 600 }}>{fileName}</a>
                                  <Typography variant="caption" display="block">{ext.toUpperCase()}</Typography>
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell sx={{ textAlign: 'center', fontSize: '0.875rem' }}>{createdAt}</TableCell>
                            <TableCell sx={{ textAlign: 'center' }}>
                              <Tooltip title="Open in new tab">
                                <IconButton size="small" onClick={() => window.open(fileUrl, '_blank', 'noopener')}>
                                  <VisibilityOutlined fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete">
                                <IconButton size="small" onClick={() => handleDelete(a.ATTACHMENT_ID)} color="error">
                                  <DeleteOutline fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25]}
                  component="div"
                  count={attachments.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  sx={{ borderTop: '1px solid #e5e7eb' }}
                />
              </>
            )}
          </>
        )}
        {/* Preview dialog for images/videos */}
        <Dialog open={previewOpen} onClose={closePreview} maxWidth="md" fullWidth>
          <DialogActions sx={{ justifyContent: 'flex-end' }}>
            <IconButton onClick={closePreview}><Close /></IconButton>
          </DialogActions>
          <DialogContent>
            {previewIsVideo ? (
              <video src={previewUrl} controls style={{ width: '100%' }} />
            ) : (
              <img src={previewUrl} alt="preview" style={{ width: '100%', height: 'auto' }} />
            )}
          </DialogContent>
        </Dialog>
      </DialogContent>
      <DialogActions sx={{ p: 2, borderTop: '1px solid #e5e7eb', bgcolor: '#fafafa' }}>
        <Button onClick={onClose} variant="contained">Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default AttachmentsPopup;
